import type { Composer } from 'vue-i18n'
import { determineLocaleCountryPrefix, parseLanguage } from '@/utils/parse'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { CCOUNTRY, CLOCALE } from '@/utils/cookie/cookieTypes'
import { LocaleSegmentRegex } from '@/enums/parse'
import type { Locale, CountryCode } from '@/types/app'
import i18nConfig from '@/config/i18n'

// It's called i19n because we can't override the i18n plugin name with the provide return
export default defineNuxtPlugin(async (nuxtApp) => {
  const $i18n = nuxtApp.$i18n as Composer
  const router = useRouter()
  const route = useRoute()
  const {
    public: { CDN_URL, LOCIZE_USE_CDN_URL, LOCIZE_PROJECT_ID, NODE_ENV, API_URL }
  } = useRuntimeConfig()
  const appStore = useAppStore()
  const authStore = useAuthStore()

  // Get cookies
  const cookieLocale = useCookie(CLOCALE).value as string | undefined
  const cookieCountry = useCookie(CCOUNTRY).value as string | undefined

  const fallbackLocale = i18nConfig.defaultLocale.toLowerCase()
  const fallbackCountry = i18nConfig.defaultCountry.toLowerCase()

  // Get accept-language header and IP for server-side detection
  let acceptLanguageHeader: string | undefined
  let requestIp: string | undefined

  if (import.meta.server) {
    const headers = useRequestHeaders(['accept-language'])
    acceptLanguageHeader = headers['accept-language'] as string | undefined
    requestIp = authStore.ipAddress
  }

  // Determine the locale-country prefix
  const localeCountryPrefix = await determineLocaleCountryPrefix({
    route: router.currentRoute.value,
    appStore,
    authStore,
    cookieLocale,
    cookieCountry,
    acceptLanguageHeader,
    requestIp
  })

  // Parse the locale from the prefix (e.g., "nl-nl" -> "nl")
  const locale = parseLanguage(localeCountryPrefix)

  /**
   * Loads locale messages for a given locale if they haven't been loaded yet.
   * Prevents memory leaks by checking if messages are already loaded before fetching.
   *
   * @param locale - The locale code to load messages for
   */
  const loadLocaleMessages = async (locale: Locale) => {
    // Check if messages for this locale are already loaded to avoid memory leaks
    const existingMessages = $i18n.getLocaleMessage(locale)
    const hasMessages = existingMessages && Object.keys(existingMessages).length > 0

    // Only load locale messages if they haven't been loaded yet
    if (!hasMessages) {
      try {
        const shouldFetchFromCDN = `${LOCIZE_USE_CDN_URL}` === '1'

        const messages = shouldFetchFromCDN
          ? ((await $fetch(`${CDN_URL}/translations/nuxt4/${locale}.json`)) as Record<string, string> | null)
          : ((await $fetch(
              `https://api.locize.app/${LOCIZE_PROJECT_ID}/${
                getIsDevelopment({ NODE_ENV, API_URL }) ? 'latest' : 'production'
              }/${locale}/translations`
            )) as Record<string, string> | null)

        if (messages) {
          $i18n.setLocale(locale)
          $i18n.setLocaleMessage(locale, messages)
          $i18n.locale.value = locale
        }
      } catch (error) {
        console.error(`Failed to load locale messages for ${locale}:`, error)
      }
    }
  }

  // Load locale messages for the determined locale
  await loadLocaleMessages(locale as Locale)

  const localePath = ({
    name,
    params = {},
    hash,
    query = {}
  }: {
    name: string
    params?: Record<string, string>
    hash?: string
    query?: Record<string, string>
  }) => {
    const routeName = `lang${name ? `-${name}` : ''}`
    const currentLocale = parseLanguage(route.params.lang as string) || appStore?.locale || fallbackLocale
    const currentCountry = parseCountry(route.params.lang as string) || appStore?.country || fallbackCountry
    const i18nParam = getI18nParam(currentLocale, currentCountry)

    const resolved = router.resolve({ name: routeName, params: { ...params, lang: i18nParam } })
    const resolvedPath = resolved.fullPath || resolved.href || resolved.path || '/'
    const stripped = removeLocalePrefix(resolvedPath)
    const normalizedSuffix = stripped.startsWith('/') ? stripped : `/${stripped}`

    let newPath = `/${i18nParam}${normalizedSuffix}`.replace(/\/{2,}/g, '/')

    Object.keys(params).forEach((key) => {
      newPath = newPath.replace(`:${key}`, String(params[key]))
    })

    if (hash) {
      newPath += `#${hash}`
    }

    if (query && Object.keys(query).length > 0) {
      newPath += `?${Object.entries(query)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')}`
    }

    return newPath
  }

  const getCurrentLocalePath = () => {
    const current = router.currentRoute.value
    return current.name ? extractRouteName(String(current.name)) : current.path
  }

  const getI18nParam = (locale: string, country: string): string => `${locale}-${country}`.toLowerCase()
  const removeLocalePrefix = (path: string): string => path.replace(LocaleSegmentRegex, '') || '/'

  const switchLocalePath = async (toLocale: string, toCountry: string) => {
    const targetCountry = toCountry.toLowerCase()
    const targetLocale = toLocale.toLowerCase()
    const targetParam = getI18nParam(targetLocale, targetCountry)

    const pathWithoutLocale = removeLocalePrefix(router.currentRoute.value.fullPath)
    const normalizedSuffix = pathWithoutLocale.startsWith('/') ? pathWithoutLocale : `/${pathWithoutLocale}`
    let newPath = `/${targetParam}${normalizedSuffix}`.replace(/\/{2,}/g, '/')

    // remove ending / if newpath has one
    if (newPath.endsWith('/')) {
      newPath = newPath.slice(0, -1)
    }

    await loadLocaleMessages(targetLocale as Locale)

    if (appStore) {
      appStore.setLanguage(targetLocale as Locale)
      appStore.setCountry(targetCountry as CountryCode)
    }

    return navigateTo(newPath)
  }

  return {
    provide: {
      i19n: {
        localePath,
        getCurrentLocalePath,
        switchLocalePath
      }
    }
  }
})
